import { useEffect, useRef, useState } from 'react';
import { config } from './config';
import { loadGoogleMaps } from './googleMaps';
import { buildPopup } from './popup';
import { huntShapes } from './hunts';
import { dotColor, drawOrder } from './scans';
import type { Group, Hunt } from './types';

const WORLD = { lat: 20, lng: 0 };
const MAX_FIT_ZOOM = 16;

export interface MapFocus {
  cellId: string;
  /** Changes on every request so focusing the same cell twice still acts. */
  nonce: number;
}

interface Props {
  groups: Group[];
  /** The filtered player's live hunts, drawn over the scan dots. */
  hunts?: Hunt[];
  onPickPlayer: (playerId: string, label: string) => void;
  focus: MapFocus | null;
}

interface Ready {
  map: google.maps.Map;
  info: google.maps.InfoWindow;
  Point: typeof google.maps.Data.Point;
  Bounds: typeof google.maps.LatLngBounds;
  Line: typeof google.maps.Polyline;
}

type Note = 'unconfigured' | 'failed' | null;

/** One dot per location, all drawn through the map's Data layer. Yellow normally, pink (drawn on top) where a multi-part part was found. */
const NO_HUNTS: Hunt[] = [];

export function ScanMap({ groups, hunts = NO_HUNTS, onPickPlayer, focus }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState<Ready | null>(null);
  const [note, setNote] = useState<Note>(config.googleMaps.apiKey === '' ? 'unconfigured' : null);
  const groupsByCell = useRef(new Map<string, Group>());
  const open = useRef<((group: Group) => void) | null>(null);
  /** The pending one-time zoom cap after a fit; removed before anything else moves the map. */
  const cap = useRef<google.maps.MapsEventListener | null>(null);
  const cancelCap = () => {
    cap.current?.remove();
    cap.current = null;
  };
  const pick = useRef(onPickPlayer);
  pick.current = onPickPlayer;

  useEffect(() => {
    if (config.googleMaps.apiKey === '') return;
    let cancelled = false;
    let made: Ready | null = null;
    (async () => {
      await loadGoogleMaps();
      const { Map, InfoWindow, Data, Polyline } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
      const { LatLngBounds, ColorScheme, SymbolPath, Size } = (await google.maps.importLibrary('core')) as google.maps.CoreLibrary;
      if (cancelled || !host.current) return;
      const map = new Map(host.current, {
        center: WORLD,
        zoom: 2,
        mapId: config.googleMaps.mapId,
        colorScheme: ColorScheme.DARK,
        gestureHandling: 'greedy',
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });
      map.data.setStyle((feature) => ({
        icon: {
          path: SymbolPath.CIRCLE,
          scale: feature.getProperty('label') ? 9 : 7,
          fillColor: String(feature.getProperty('color')),
          fillOpacity: 1,
          strokeColor: feature.getProperty('label') ? '#ffffff' : '#1b1f2a',
          strokeWeight: feature.getProperty('label') ? 2 : 1,
        },
        zIndex: Number(feature.getProperty('z')),
        clickable: true,
        cursor: 'pointer',
      }));
      const info = new InfoWindow({ minWidth: 260, maxWidth: 380, pixelOffset: new Size(0, -10) });
      made = { map, info, Point: Data.Point, Bounds: LatLngBounds, Line: Polyline };
      setReady(made);
    })().catch(() => {
      if (!cancelled) setNote('failed');
    });
    return () => {
      cancelled = true;
      cancelCap();
      made?.info.close();
      made?.map.data.forEach((f) => made?.map.data.remove(f));
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    const { map, info, Point, Bounds } = ready;
    const openPanel = (group: Group) => {
      info.setContent(buildPopup(group, (id, label) => pick.current(id, label)));
      info.setPosition({ lat: group.lat, lng: group.lng });
      info.open({ map });
    };
    open.current = openPanel;
    cancelCap();
    info.close();
    map.data.forEach((f) => map.data.remove(f));
    groupsByCell.current.clear();
    for (const group of drawOrder(groups)) {
      map.data.add({
        geometry: new Point({ lat: group.lat, lng: group.lng }),
        properties: { cellId: group.cellId, color: dotColor(group), z: group.hasMultiPart ? 1 : 0 },
      });
      groupsByCell.current.set(group.cellId, group);
    }
    if (groups.length === 0) {
      map.setCenter(WORLD);
      map.setZoom(2);
      return;
    }
    const distinct = new Set(groups.map((g) => `${g.lat},${g.lng}`));
    if (distinct.size === 1) {
      // A zero-size fit would zoom to the maximum and snap back; centre at the cap instead.
      map.setCenter({ lat: groups[0].lat, lng: groups[0].lng });
      map.setZoom(MAX_FIT_ZOOM);
      return;
    }
    const bounds = new Bounds();
    for (const g of groups) bounds.extend({ lat: g.lat, lng: g.lng });
    map.fitBounds(bounds, 48);
    cap.current = google.maps.event.addListenerOnce(map, 'idle', () => {
      cap.current = null;
      if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
    });
  }, [ready, groups]);

  /** Hunt dots (Data layer, above the scan dots) and the lines joining them. Refit to include them. */
  useEffect(() => {
    if (!ready) return;
    const { map, Point, Line, Bounds } = ready;
    const { dots, lines } = huntShapes(hunts);
    const added = dots.map((d) =>
      map.data.add({ geometry: new Point({ lat: d.lat, lng: d.lng }), properties: { color: d.color, z: 3, label: d.label } }),
    );
    const drawn = lines.map(
      (l) => new Line({ map, path: [l.from, l.to], strokeColor: '#e5252a', strokeOpacity: 0.85, strokeWeight: 3, zIndex: 2, clickable: false }),
    );
    if (dots.length > 0) {
      cancelCap();
      const bounds = new Bounds();
      for (const g of groups) bounds.extend({ lat: g.lat, lng: g.lng });
      for (const d of dots) bounds.extend({ lat: d.lat, lng: d.lng });
      map.fitBounds(bounds, 48);
      cap.current = google.maps.event.addListenerOnce(map, 'idle', () => {
        cap.current = null;
        if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
      });
    }
    return () => {
      for (const f of added) map.data.remove(f);
      for (const l of drawn) l.setMap(null);
    };
  }, [ready, hunts, groups]);

  useEffect(() => {
    if (!ready) return;
    const listener = ready.map.data.addListener('click', (e: google.maps.Data.MouseEvent) => {
      const label = e.feature.getProperty('label');
      if (label && e.latLng) {
        // A DOM node with its own dark colour: a bare string inherits the dark map theme's light text.
        const tip = document.createElement('div');
        tip.className = 'hunt-tip';
        tip.textContent = String(label);
        ready.info.setContent(tip);
        ready.info.setPosition(e.latLng);
        ready.info.open({ map: ready.map });
        return;
      }
      const group = groupsByCell.current.get(String(e.feature.getProperty('cellId')));
      if (group) open.current?.(group);
    });
    return () => listener.remove();
  }, [ready]);

  useEffect(() => {
    if (!ready || !focus) return;
    const group = groupsByCell.current.get(focus.cellId);
    if (!group) return;
    cancelCap();
    ready.map.setCenter({ lat: group.lat, lng: group.lng });
    ready.map.setZoom(Math.max(ready.map.getZoom() ?? 0, 15));
    open.current?.(group);
  }, [ready, focus]);

  return (
    <>
      <div ref={host} className="map" data-testid="map" />
      {note === 'unconfigured' && <p className="map-note" role="status">Google Maps is not configured</p>}
      {note === 'failed' && <p className="map-note" role="status">Google Maps failed to load</p>}
    </>
  );
}
